import { useCallback, type CSSProperties } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUiScale } from '@/contexts/DeviceModeContext';
import { useAuthModal } from '@/contexts/AuthModalContext';
import { useSupportUnread } from '@/features/support/hooks/useSupportUnread';
import { playClickSound } from '@/utils/soundUtils';
import { CountBadge, cx, focusRing } from '@/components/ui';
import { TAB_ICONS as ICONS, TABS, activeTab } from '@/layouts/navTabs';


/** Bottom tab bar on the compact layout (phones, iPad portrait, portrait kiosk); top tabs otherwise. */
export default function BottomNav() {
  const { layout } = useUiScale();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { requireAuth } = useAuthModal();
  /** Tickets with a support reply the Player has not opened yet (0 for guests): shown on Help. */
  const supportUnread = useSupportUnread();
  const active = activeTab(location.pathname);
  const activeIndex = TABS.findIndex((tab) => tab.id === active);

  const go = useCallback(
    (route: string, gated: boolean) => {
      playClickSound();
      if (gated) requireAuth(() => navigate(route));
      else navigate(route);
    },
    [navigate, requireAuth]
  );

  if (layout !== 'compact') return null;

  return (
    <nav
      // .bottom-nav (globals.css) sets the layer above <main> and drives the sliding indicator
      className="bottom-nav relative flex w-full shrink-0 items-stretch border-t border-edge-subtle bg-chrome backdrop-blur-md safe-area-bottom"
      role="navigation"
      aria-label={t('nav.bottom', 'Primary')}
      style={{ '--bn-count': TABS.length, '--bn-index': activeIndex } as CSSProperties}
    >
      {activeIndex >= 0 && <span className="bottom-nav__indicator" aria-hidden />}
      {TABS.map(({ id, to, gated, labelKey, tourId }) => {
        const isActive = active === id;
        const Icon = ICONS[id];
        return (
          <button
            key={id}
            type="button"
            onClick={() => go(to, gated)}
            aria-current={isActive ? 'page' : undefined}
            data-tour-id={tourId}
            className={cx(
              'bottom-nav__tab flex min-h-[var(--wf-bottom-nav-h,3.5rem)] flex-1 flex-col items-center justify-center gap-1 font-ui',
              focusRing,
              'focus-visible:ring-inset',
              isActive ? 'is-active text-primary' : 'text-tertiary'
            )}
          >
            <span className="relative inline-flex">
              <Icon className="size-6" aria-hidden />
              {id === 'help' && supportUnread > 0 ? (
                <span className="pointer-events-none absolute -right-3 -top-2">
                  <CountBadge
                    count={supportUnread}
                    max={9}
                    label={t('supportDesk.page.unreadBadgeLabel', { count: supportUnread })}
                  />
                </span>
              ) : null}
            </span>
            <span className="bottom-nav__label leading-none">{t(labelKey)}</span>
          </button>
        );
      })}
    </nav>
  );
}
